"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { Button } from "../../../elements/button/button";
import { cn } from "../../../lib/utils";
import {
  mainNavigationClasses,
  mobileNavigationBarClasses,
  mobileNavigationMenuId,
} from "../main-navigation.constants";
import type {
  MainNavigationColumn,
  MainNavigationItem,
  MainNavigationLink,
  MainNavigationLinkItem,
  MainNavigationLinkTarget,
  MainNavigationTeaser,
} from "../main-navigation.types";
import { NavigationServiceLinks } from "../navigation-service-links/navigation-service-links";
import { NavigationTeaser } from "../navigation-teaser/navigation-teaser";
import {
  getNavigationTrackingAttributes,
  type NavigationLevel,
} from "../navigation-tracking";

export interface MobileNavigationProps {
  items: MainNavigationItem[];
  open: boolean;
  onClose: () => void;
  /** Service links of the navigation itself, shown on the first level. */
  serviceLinks?: MainNavigationLink[];
  /** Teaser of the navigation itself, shown on the first level. */
  teaser?: MainNavigationTeaser;
  /** Must match the `aria-controls` of the toggle in the navigation bar. */
  id?: string;
  microcopies: {
    navigationLabel: string;
    menuTitle: string;
    back: string;
    overview: string;
  };
}

/** The level the menu currently shows, by the entries drilled into so far. */
interface MobileNavigationPath {
  itemId?: string;
  columnId?: string;
  linkItemId?: string;
}

/** Drilling in enters from the right, going back from the left. */
type MobileNavigationDirection = "forward" | "back";

const LEVEL_ANIMATIONS: Record<MobileNavigationDirection, string> = {
  forward: "motion-safe:animate-nav-level-forward",
  back: "motion-safe:animate-nav-level-back",
};

interface MobileNavigationEntry {
  id: string;
  label: string;
  href?: string;
  target?: MainNavigationLinkTarget;
  tracking?: Record<string, string | number>;
  /** Set on entries that open a deeper level instead of following a link. */
  onDrillDown?: () => void;
}

interface MobileNavigationLevel {
  /** Announced on entering the level; the design shows no visible title. */
  title: string;
  entries: MobileNavigationEntry[];
  /** Link below the entries, e.g. the column's "see all..." or an overview. */
  cta?: MainNavigationLink & { ariaLabel?: string };
  teaser?: MainNavigationTeaser;
  serviceLinks?: MainNavigationLink[];
  onBack?: () => void;
  /** Entries that drill down sit further apart than plain link lists. */
  spacing: "wide" | "narrow";
}

const SUB_ITEM_LEVEL = 3;
const ENTRY_ATTRIBUTE = "data-mobile-navigation-entry";
const TITLE_ATTRIBUTE = "data-mobile-navigation-title";
// Sub-pixel scroll positions never quite reach the end of the list.
const SCROLL_END_TOLERANCE_PX = 1;

/**
 * Full screen menu below the desktop breakpoint. It drills down one level at a
 * time: level 1 items, then the columns of their mega menu, then the entries of
 * a column and their level 3 entries. It is not a modal dialog: the navigation
 * bar stays outside it so that search, cart and account remain reachable.
 */
export function MobileNavigation(props: Readonly<MobileNavigationProps>) {
  // The direction stays unset on the first level so the menu does not slide in
  // on top of the panel's own opening animation.
  const [{ path, direction }, setLevelState] = useState<{
    path: MobileNavigationPath;
    direction?: MobileNavigationDirection;
  }>({ path: {} });
  const navRef = useRef<HTMLElement>(null);
  const { open, onClose } = props;

  // Reopening the menu starts at the first level again.
  useEffect(() => {
    if (!open) {
      setLevelState({ path: {} });
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    function handleKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // The menu covers the page, so the page behind it must not scroll along.
  useEffect(() => {
    if (!open) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  // Moves focus to the new level so screen reader users hear where they are.
  function showLevel(
    nextPath: MobileNavigationPath,
    nextDirection: MobileNavigationDirection,
    focusTarget: { attribute: string; value?: string }
  ) {
    flushSync(() =>
      setLevelState({ path: nextPath, direction: nextDirection })
    );
    focusIn(navRef.current, focusTarget);
  }

  useEffect(() => {
    if (open) {
      focusIn(navRef.current, { attribute: TITLE_ATTRIBUTE });
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const level = getLevel(props, path, showLevel);
  // Remounts on every level change so the entering animation restarts.
  const levelKey = [path.itemId, path.columnId, path.linkItemId].join("/");
  const levelAnimation = direction ? LEVEL_ANIMATIONS[direction] : undefined;

  return (
    // On tall portrait screens only the entries scroll, so that the buttons and
    // the teaser stay in place below long lists. Elsewhere a full width teaser
    // would leave the entries no room, so the whole menu scrolls instead.
    <div
      className={cn(
        "fixed inset-x-0 top-[var(--site-preview-offset,0px)] bottom-0 z-40 mobile-nav-pinned:overflow-hidden overflow-y-auto bg-white motion-safe:animate-nav-panel-in lg:hidden",
        mobileNavigationBarClasses.panelOffset
      )}
      data-nav-context="mobile_menu"
      data-testid="mobile-navigation"
      id={props.id ?? mobileNavigationMenuId}
    >
      <nav
        aria-label={props.microcopies.navigationLabel}
        className="flex mobile-nav-pinned:h-full min-h-full flex-col gap-6 px-3 pt-15"
        ref={navRef}
      >
        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col justify-between gap-10",
            levelAnimation
          )}
          key={`entries-${levelKey}`}
        >
          <MobileNavigationScrollArea>
            {/* Receives focus after a level change; it is not interactive. */}
            <h2
              className="sr-only outline-none"
              data-mobile-navigation-title=""
              tabIndex={-1}
            >
              {level.title}
            </h2>
            <ul
              className={cn(
                "flex flex-col",
                level.spacing === "wide" ? "gap-8" : "gap-6"
              )}
            >
              {level.entries.map((entry) => (
                <li key={entry.id}>
                  <MobileEntry entry={entry} onLinkClick={onClose} />
                </li>
              ))}
            </ul>
            {level.cta ? (
              <Button
                aria-label={level.cta.ariaLabel}
                className={mainNavigationClasses.mobileCta}
                href={level.cta.href}
                mode="D2C"
                onClick={onClose}
                target={level.cta.target}
                unstyled={true}
              >
                {level.cta.label}
              </Button>
            ) : null}
          </MobileNavigationScrollArea>

          <div className="flex min-h-[42px] items-start gap-2">
            {level.serviceLinks?.length ? (
              <NavigationServiceLinks
                links={level.serviceLinks}
                onLinkClick={onClose}
                size="sm"
              />
            ) : null}
            {level.onBack ? (
              <Button
                className="ml-auto"
                mode="D2C"
                onClick={level.onBack}
                size="sm"
                variant="outline"
              >
                <ChevronLeft aria-hidden="true" />
                {props.microcopies.back}
              </Button>
            ) : null}
          </div>
        </div>

        {level.teaser ? (
          // Keeps its aspect ratio instead of shrinking with the level above it.
          <div
            className={cn("shrink-0", levelAnimation)}
            key={`teaser-${levelKey}`}
          >
            <NavigationTeaser
              aspectRatio="16:9"
              onLinkClick={onClose}
              sizes="100vw"
              teaser={level.teaser}
              variant="compact"
            />
          </div>
        ) : null}
      </nav>
    </div>
  );
}

/**
 * Scrolls the entries of a level where the menu keeps its buttons and teaser
 * in place, and fades them out at the bottom while more follow. Remounted with
 * each level, so every level starts at the top.
 */
function MobileNavigationScrollArea(props: Readonly<{ children: ReactNode }>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  const updateFade = useCallback(() => {
    const element = scrollRef.current;
    setHasMoreBelow(
      element !== null &&
        element.scrollTop + element.clientHeight <
          element.scrollHeight - SCROLL_END_TOLERANCE_PX
    );
  }, []);

  useEffect(() => {
    updateFade();
    window.addEventListener("resize", updateFade);
    return () => window.removeEventListener("resize", updateFade);
  }, [updateFade]);

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      {/* The negative margin keeps focus outlines clear of the clipping edge. */}
      <div
        className="-m-2 flex min-h-0 flex-1 flex-col gap-8 mobile-nav-pinned:overflow-y-auto p-2"
        data-testid="mobile-navigation-entries"
        onScroll={updateFade}
        ref={scrollRef}
      >
        {props.children}
      </div>
      {hasMoreBelow ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-linear-to-t from-white to-transparent"
          data-testid="mobile-navigation-scroll-fade"
        />
      ) : null}
    </div>
  );
}

function MobileEntry(
  props: Readonly<{
    entry: MobileNavigationEntry;
    onLinkClick: () => void;
  }>
) {
  if (props.entry.onDrillDown) {
    return (
      <Button
        className={mainNavigationClasses.mobileEntry}
        data-mobile-navigation-entry={props.entry.id}
        {...props.entry.tracking}
        mode="D2C"
        onClick={props.entry.onDrillDown}
        unstyled={true}
      >
        {props.entry.label}
        <ChevronRight aria-hidden="true" className="size-5 shrink-0" />
      </Button>
    );
  }

  return (
    <Button
      className={mainNavigationClasses.mobileEntry}
      data-mobile-navigation-entry={props.entry.id}
      {...props.entry.tracking}
      href={props.entry.href}
      mode="D2C"
      onClick={props.onLinkClick}
      target={props.entry.target}
      unstyled={true}
    >
      {props.entry.label}
    </Button>
  );
}

/**
 * Builds the level the current path points at, falling back to the level above
 * whenever a path segment no longer resolves.
 */
function getLevel(
  props: Readonly<MobileNavigationProps>,
  path: MobileNavigationPath,
  showLevel: (
    nextPath: MobileNavigationPath,
    nextDirection: MobileNavigationDirection,
    focusTarget: { attribute: string; value?: string }
  ) => void
): MobileNavigationLevel {
  const item = props.items.find((candidate) => candidate.id === path.itemId);
  const megaMenu = item?.megaMenu;
  const columns = megaMenu
    ? [...megaMenu.columns, ...(megaMenu.secondaryColumns ?? [])]
    : [];
  const column = columns.find((candidate) => candidate.id === path.columnId);
  const linkItem = column?.items.find(
    (candidate) =>
      candidate.id === path.linkItemId && candidate.subItems?.length
  );
  // Every screen of an item may carry its own teaser; screens without one show
  // the teaser of the screen above, up to the item's screen. The desktop
  // teasers of the mega menu do not carry over.
  const columnTeaser = column?.teaser ?? megaMenu?.mobileTeaser;

  function drillDown(nextPath: MobileNavigationPath) {
    showLevel(nextPath, "forward", { attribute: TITLE_ATTRIBUTE });
  }

  function goBack(nextPath: MobileNavigationPath, focusedEntryId: string) {
    showLevel(nextPath, "back", {
      attribute: ENTRY_ATTRIBUTE,
      value: focusedEntryId,
    });
  }

  // Level 3: the sub items of a level 2 entry.
  if (item && column && linkItem) {
    return {
      title: linkItem.label,
      entries: (linkItem.subItems ?? []).map((subItem) =>
        toLinkEntry(subItem, SUB_ITEM_LEVEL)
      ),
      cta: toOverviewCta(props.microcopies.overview, linkItem),
      teaser: columnTeaser,
      onBack: () =>
        goBack({ itemId: item.id, columnId: column.id }, linkItem.id),
      spacing: "narrow",
    };
  }

  // Level 2: the entries of one mega menu column.
  if (item && column) {
    return {
      title: column.headline ?? item.label,
      entries: column.items.map((candidate) =>
        toColumnEntry(column, candidate, drillDown, item.id)
      ),
      cta: column.cta,
      teaser: columnTeaser,
      onBack: () => goBack({ itemId: item.id }, column.id),
      spacing: "narrow",
    };
  }

  // Level 1: the columns of a mega menu. Columns without a headline have no
  // label to drill into, so their entries are listed here directly.
  if (item && megaMenu) {
    return {
      title: item.label,
      entries: columns.flatMap((candidate) =>
        candidate.headline
          ? [
              {
                id: candidate.id,
                label: candidate.headline,
                onDrillDown: () =>
                  drillDown({ itemId: item.id, columnId: candidate.id }),
              },
            ]
          : candidate.items.map((linkItemCandidate) =>
              toColumnEntry(candidate, linkItemCandidate, drillDown, item.id)
            )
      ),
      cta: toOverviewCta(props.microcopies.overview, item),
      teaser: megaMenu.mobileTeaser,
      onBack: () => goBack({}, item.id),
      spacing: "wide",
    };
  }

  return {
    title: props.microcopies.menuTitle,
    entries: props.items.map((candidate) =>
      candidate.megaMenu
        ? {
            id: candidate.id,
            label: candidate.label,
            tracking: getNavigationTrackingAttributes(candidate, 1),
            onDrillDown: () => drillDown({ itemId: candidate.id }),
          }
        : toLinkEntry(candidate, 1)
    ),
    teaser: props.teaser,
    serviceLinks: props.serviceLinks,
    spacing: "wide",
  };
}

/**
 * The design has no visible level title, so the link to the level's own page
 * spells out what it is an overview of.
 */
function toOverviewCta(
  overview: string,
  target: { label: string; href?: string; target?: MainNavigationLinkTarget }
): MobileNavigationLevel["cta"] {
  if (!target.href) {
    return;
  }

  return {
    label: overview,
    ariaLabel: `${overview}: ${target.label}`,
    href: target.href,
    target: target.target,
  };
}

function toLinkEntry(
  item: {
    id: string;
    label: string;
    href?: string;
    target?: MainNavigationLinkTarget;
    internalName?: string;
    index?: number;
  },
  level: NavigationLevel
): MobileNavigationEntry {
  return {
    id: item.id,
    label: item.label,
    href: item.href,
    target: item.target,
    tracking: getNavigationTrackingAttributes(item, level),
  };
}

function toColumnEntry(
  column: MainNavigationColumn,
  linkItem: MainNavigationLinkItem,
  drillDown: (nextPath: MobileNavigationPath) => void,
  itemId: string
): MobileNavigationEntry {
  if (!linkItem.subItems?.length) {
    return toLinkEntry(linkItem, 2);
  }

  return {
    id: linkItem.id,
    label: linkItem.label,
    tracking: getNavigationTrackingAttributes(linkItem, 2),
    onDrillDown: () =>
      drillDown({ itemId, columnId: column.id, linkItemId: linkItem.id }),
  };
}

function focusIn(
  container: HTMLElement | null,
  focusTarget: { attribute: string; value?: string }
): void {
  Array.from(
    container?.querySelectorAll<HTMLElement>(`[${focusTarget.attribute}]`) ?? []
  )
    .find(
      (element) =>
        focusTarget.value === undefined ||
        element.getAttribute(focusTarget.attribute) === focusTarget.value
    )
    ?.focus();
}
